import { useState } from 'react';
import { Tab, TabProps } from 'react-admin';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import NoteCard from './NoteCard';
import NoteFormDialog from './NoteFormDialog';
import NoteHistoryDialog from './NoteHistoryDialog';
import useController from './useController';
import { NoteFormValues, NoteRecord } from './types';

const NotesTab = (props: Partial<TabProps>) => {
  const { notes, loading, history, historyLoading, createNote, updateNote, deleteNote, fetchHistory } = useController();

  const [formOpen, setFormOpen] = useState(false);
  const [editingNote, setEditingNote] = useState<NoteRecord | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);

  const handleAdd = () => {
    setEditingNote(null);
    setFormOpen(true);
  };

  const handleEdit = (note: NoteRecord) => {
    setEditingNote(note);
    setFormOpen(true);
  };

  const handleDelete = (noteId: number) => {
    if (window.confirm('Are you sure you want to delete this note?')) {
      deleteNote(noteId);
    }
  };

  const handleHistory = (noteId: number) => {
    fetchHistory(noteId);
    setHistoryOpen(true);
  };

  const handleSave = (values: NoteFormValues) => {
    if (editingNote) {
      updateNote(editingNote.id, values);
    } else {
      createNote(values);
    }
  };

  return (
    <Tab label="Notes" {...props}>
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Notes</Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd} size="small">
            Add Note
          </Button>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : notes.length === 0 ? (
          <Typography color="text.secondary">No notes yet.</Typography>
        ) : (
          notes.map((note) => (
            <NoteCard key={note.id} note={note} onEdit={handleEdit} onDelete={handleDelete} onHistory={handleHistory} />
          ))
        )}
      </Box>

      <NoteFormDialog open={formOpen} onClose={() => setFormOpen(false)} onSave={handleSave} note={editingNote} />

      <NoteHistoryDialog
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        history={history}
        loading={historyLoading}
      />
    </Tab>
  );
};

export default NotesTab;
